import { useAuth, useLocalStorage } from "@elit-hair/elit-tools";
import { DateTime } from "luxon";
import { useCallback, useEffect, useRef, useState } from "react";

import { readDarwinDraft, writeDarwinDraft } from "@/components/ai-chat/darwin-drafts";
import { useDarwinPageCatalog } from "@/components/ai-chat/use-darwin-page-catalog";
import { useDarwinPageContext } from "@/components/ai-chat/use-darwin-page-context";
import { LOCAL_STORAGE_KEYS } from "@/constants/local-storage-keys";
import { darwinService } from "@/services/darwinService";
import {
    ChatMessageRating,
    ChatMessageRole,
    ChatMessage as ChatMessageType,
    StreamEventData,
} from "@/types/chatbot.types";

const NO_CONVERSATION = undefined;
const NEW_CONVERSATION_DRAFT_KEY = "new";

const STREAM_ERROR_MESSAGE = "Sorry, there was an error processing your message. Please try again.";

function withoutStreamErrors(messages: ChatMessageType[]): ChatMessageType[] {
    return messages.filter((message) => message.content !== STREAM_ERROR_MESSAGE);
}

export type UseChatStreamReturn = {
    messages: ChatMessageType[];
    inputRef: React.RefObject<HTMLTextAreaElement>;
    defaultInput: string;
    hasInput: boolean;
    inputRevision: number;
    handleInputChange: (value: string) => void;
    handleSubmit: (e?: React.FormEvent<HTMLFormElement>) => Promise<void>;
    clearHistory: () => void;
    rateMessage: (params: { messageId: string; rating?: ChatMessageRating }) => void;
    markToolRequested: (params: { messageId: string; incidentId: string }) => void;
    conversationId?: string;
    isLoading: boolean;
    isStreaming: boolean;
    stopStreaming: () => void;
};

/** Replaces the message with `id`, or appends it when it is not in the list yet. */
function upsertMessage(messages: ChatMessageType[], message: ChatMessageType): ChatMessageType[] {
    const index = messages.findIndex((existing) => existing.id === message.id);
    if (index === -1) return [...messages, message];

    const updated = [...messages];
    updated[index] = message;
    return updated;
}

/**
 * Darwin chat state. Each turn streams the reply from the Darwin agent (elitai-svc), which
 * stores the conversation; local storage keeps the messages and the conversation id per user.
 */
export function useChatStream() {
    const { auth, userData } = useAuth();
    const page = useDarwinPageContext();
    const pages = useDarwinPageCatalog();
    const currentUserId = userData?.id || "anonymous";

    const [allUserHistories, setAllUserHistories] = useLocalStorage<Record<string, ChatMessageType[]>>(
        LOCAL_STORAGE_KEYS.AI_CHAT_HISTORY.key,
        {}
    );

    const [conversationIds, setConversationIds] = useLocalStorage<Record<string, string>>(
        LOCAL_STORAGE_KEYS.AI_CHAT_CONVERSATION_IDS.key,
        {}
    );
    const conversationId = conversationIds[currentUserId] ?? NO_CONVERSATION;

    useEffect(() => {
        if (!conversationId) return;

        let cancelled = false;
        darwinService.getRequestedToolMessageIds(conversationId).then(
            (messageIds) => {
                if (cancelled || messageIds.length === 0) return;
                const requested = new Set(messageIds);
                setMessages((current) =>
                    current.map((message) => {
                        if (!requested.has(message.id) || message.toolRequest?.incidentId) return message;
                        return {
                            ...message,
                            toolRequest: {
                                summary: message.toolRequest?.summary ?? "",
                                incidentId: message.id,
                            },
                        };
                    })
                );
            },
            () => undefined
        );

        return () => {
            cancelled = true;
        };
    }, [conversationId]);

    const [messages, setMessages] = useState<ChatMessageType[]>(
        withoutStreamErrors(allUserHistories[currentUserId] || [])
    );
    // Unsent text survives reloads and closing the panel, per conversation.
    // The string lives on the textarea; this ref is what submit and drafts read, so typing does not re-render the chat.
    const draftKey = `${currentUserId}:${conversationId ?? NEW_CONVERSATION_DRAFT_KEY}`;
    const inputRef = useRef<HTMLTextAreaElement>(null);
    const inputValueRef = useRef(readDarwinDraft(draftKey));
    const defaultInput = useRef(inputValueRef.current).current;
    const [hasInput, setHasInput] = useState(() => inputValueRef.current.trim().length > 0);
    const [inputRevision, setInputRevision] = useState(0);

    const [isLoading, setIsLoading] = useState(false);
    const [isStreaming, setIsStreaming] = useState(false);

    const streamingMessageRef = useRef<ChatMessageType | null>(null);
    const abortControllerRef = useRef<AbortController | null>(null);

    useEffect(() => {
        setAllUserHistories((prev) => ({ ...prev, [currentUserId]: messages }));
    }, [messages, currentUserId]);

    useEffect(() => {
        setMessages(withoutStreamErrors(allUserHistories[currentUserId] || []));
    }, [currentUserId]);

    // Which draft the text in the box belongs to; it follows the conversation when one starts or is cleared
    const inputDraftKeyRef = useRef(draftKey);
    const inputUserIdRef = useRef(currentUserId);
    const draftTimerRef = useRef<number | null>(null);

    const clearDraftTimer = () => {
        if (draftTimerRef.current === null) return;
        window.clearTimeout(draftTimerRef.current);
        draftTimerRef.current = null;
    };

    const replaceInput = useCallback((value: string) => {
        inputValueRef.current = value;
        const textarea = inputRef.current;
        if (textarea && textarea.value !== value) textarea.value = value;
        clearDraftTimer();
        writeDarwinDraft({ draftKey: inputDraftKeyRef.current, value });
        setHasInput(value.trim().length > 0);
        setInputRevision((revision) => revision + 1);
    }, []);

    useEffect(() => {
        const previousDraftKey = inputDraftKeyRef.current;
        if (previousDraftKey === draftKey) return;
        clearDraftTimer();
        inputDraftKeyRef.current = draftKey;

        if (inputUserIdRef.current !== currentUserId) {
            // Another user signed in: show their draft, never carry this one over
            inputUserIdRef.current = currentUserId;
            replaceInput(readDarwinDraft(draftKey));
            return;
        }
        writeDarwinDraft({ draftKey: previousDraftKey, value: "" });
        writeDarwinDraft({ draftKey, value: inputValueRef.current });
    }, [draftKey, currentUserId, replaceInput]);

    const finishStream = useCallback(() => {
        setIsLoading(false);
        setIsStreaming(false);
        streamingMessageRef.current = null;
        abortControllerRef.current = null;
    }, []);

    const handleInputChange = useCallback((value: string) => {
        inputValueRef.current = value;
        clearDraftTimer();
        draftTimerRef.current = window.setTimeout(() => {
            draftTimerRef.current = null;
            writeDarwinDraft({ draftKey: inputDraftKeyRef.current, value: inputValueRef.current });
        }, 400);
        const nextHasInput = value.trim().length > 0;
        setHasInput((current) => (current === nextHasInput ? current : nextHasInput));
    }, []);

    useEffect(() => {
        return () => {
            clearDraftTimer();
            writeDarwinDraft({ draftKey: inputDraftKeyRef.current, value: inputValueRef.current });
        };
    }, []);

    const handleStreamEvent = useCallback(
        (event: StreamEventData) => {
            if (event.type === "conversation") {
                setConversationIds((prev) => ({ ...prev, [currentUserId]: event.data }));
                return;
            }

            const streamingMessage = streamingMessageRef.current;
            if (!streamingMessage) return;

            if (event.type === "tool_request") {
                const summary = JSON.parse(event.data).summary;
                if (!summary) return;
                streamingMessage.toolRequest = { summary };
                setMessages((current) => upsertMessage(current, { ...streamingMessage }));
                return;
            }

            if (event.type !== "token") return;

            streamingMessage.content += event.data;
            const snapshot = { ...streamingMessage };
            setMessages((current) => upsertMessage(current, snapshot));
        },
        [currentUserId]
    );

    const handleSubmit = useCallback(
        async (e?: React.FormEvent<HTMLFormElement>) => {
            e?.preventDefault();

            const content = inputValueRef.current.trim();
            if (!content || isLoading || isStreaming) return;

            abortControllerRef.current?.abort();

            const userMessage: ChatMessageType = {
                id: DateTime.now().toMillis().toString(),
                role: ChatMessageRole.USER,
                content,
                createdAt: DateTime.now().toISO(),
            };
            const streamingMessageId = (DateTime.now().toMillis() + 1).toString();
            const abortController = new AbortController();

            streamingMessageRef.current = {
                id: streamingMessageId,
                role: ChatMessageRole.ASSISTANT,
                content: "",
                createdAt: DateTime.now().toISO(),
            };
            abortControllerRef.current = abortController;

            setMessages((current) => [...current, userMessage]);
            replaceInput("");
            setIsLoading(true);
            setIsStreaming(true);

            await darwinService.streamReply(
                {
                    conversationId,
                    message: { id: userMessage.id, content },
                    replyMessageId: streamingMessageId,
                    page,
                    pages,
                    auth,
                    abortController,
                },
                {
                    onEvent: handleStreamEvent,
                    onComplete: finishStream,
                    onError: (error) => {
                        console.error("Error streaming Darwin response:", error);
                        const missingDarwin = error.message.includes("status: 404");
                        if (missingDarwin) {
                            finishStream();
                            return;
                        }
                        const errorMessage: ChatMessageType = {
                            id: streamingMessageId,
                            role: ChatMessageRole.ASSISTANT,
                            content: STREAM_ERROR_MESSAGE,
                            createdAt: DateTime.now().toISO(),
                        };
                        setMessages((current) => upsertMessage(current, errorMessage));
                        finishStream();
                    },
                }
            );
        },
        [isLoading, isStreaming, conversationId, page, pages, auth, handleStreamEvent, finishStream, replaceInput]
    );

    const stopStreaming = useCallback(() => {
        if (!abortControllerRef.current) return;
        abortControllerRef.current.abort();
        finishStream();
    }, [finishStream]);

    useEffect(() => {
        return () => abortControllerRef.current?.abort();
    }, []);

    const clearHistory = useCallback(() => {
        stopStreaming();
        setMessages([]);
        setConversationIds((prev) => {
            const updated = { ...prev };
            delete updated[currentUserId];
            return updated;
        });
        setAllUserHistories((prev) => {
            const updated = { ...prev };
            delete updated[currentUserId];
            return updated;
        });
    }, [stopStreaming, currentUserId]);

    const markToolRequested = useCallback(({ messageId, incidentId }: { messageId: string; incidentId: string }) => {
        setMessages((current) =>
            current.map((message) =>
                message.id === messageId && message.toolRequest
                    ? { ...message, toolRequest: { ...message.toolRequest, incidentId } }
                    : message
            )
        );
    }, []);

    const rateMessage = useCallback(({ messageId, rating }: { messageId: string; rating?: ChatMessageRating }) => {
        setMessages((current) =>
            current.map((message) => (message.id === messageId ? { ...message, rating } : message))
        );
    }, []);

    return {
        messages,
        inputRef,
        defaultInput,
        hasInput,
        inputRevision,
        handleInputChange,
        handleSubmit,
        clearHistory,
        rateMessage,
        markToolRequested,
        conversationId,
        isLoading,
        isStreaming,
        stopStreaming,
    } satisfies UseChatStreamReturn;
}
